declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date | Record<string, any>,
      config?: Record<string, any>
    ) => void;
  }
}

interface NavClickParams {
  navItem: string;
  navUrl: string;
}

interface MegaMenuLinkClickParams {
  menu: string;
  linkText: string;
  linkUrl: string;
}

interface FeaturedCardClickParams {
  menu: string;
  cardTitle: string;
  cardUrl: string;
}

export function trackNavClick(params: NavClickParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "nav_click", {
      nav_item: params.navItem,
      nav_url: params.navUrl,
    });
  }
}

export function trackMegaMenuLinkClick(params: MegaMenuLinkClickParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "mega_menu_link_click", {
      menu: params.menu,
      link_text: params.linkText,
      link_url: params.linkUrl,
    });
  }
}

export function trackFeaturedCardClick(params: FeaturedCardClickParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "featured_card_click", {
      menu: params.menu,
      card_title: params.cardTitle,
      card_url: params.cardUrl,
    });
  }
}

interface PathwayClickParams {
  pathwayName: string;
  ctaText: string;
  ctaUrl: string;
}

interface AdvisorCTAParams {
  source: string;
}

interface FastTrackReferralParams {
  pathway: string;
}

export function trackPathwayClick(params: PathwayClickParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "pathway_click", {
      pathway_name: params.pathwayName,
      cta_text: params.ctaText,
      cta_url: params.ctaUrl,
    });
  }
}

export function trackAdvisorCTA(params: AdvisorCTAParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "advisor_cta_click", {
      source: params.source,
    });
  }
}

export function trackFastTrackReferral(params: FastTrackReferralParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "fast_track_referral", {
      pathway: params.pathway,
    });
  }
}

interface PathwaySelectorEventParams {
  eventName: string;
  [key: string]: string | number | boolean | undefined;
}

export function trackPathwaySelectorEvent(params: PathwaySelectorEventParams) {
  const { eventName, ...rest } = params;

  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, {
      page: "pathway_selector",
      ...rest,
    });
  }

  if (typeof window !== "undefined" && (window as any).heap) {
    (window as any).heap.track(eventName, {
      page: "pathway_selector",
      ...rest,
    });
  }
}

/**
 * Public, privacy-safe events for the shared training guidance journeys.
 * Keep these fields to short, enumerated page/persona/course/step keys. Do not
 * pass form answers, contact details, free text, or full URLs.
 */
export type JourneyEventName =
  | "persona_selected"
  | "pathway_started"
  | "pathway_step_completed"
  | "pathway_result_viewed"
  | "pathway_course_clicked"
  | "pathway_team_redirected"
  | "pathway_enquiry_form_started"
  | "team_enquiry_started"
  | "generate_lead"
  | "enquiry_submit_failed"
  /** @deprecated Kept for reporting continuity with older journey emitters. */
  | "form_submission_failed";

export type JourneyEventProps = Partial<{
  persona: string;
  placement: string;
  destination_page_key: string;
  entry_page_key: string;
  page_key: string;
  course_key: string;
  step_key: string;
  booking_intent: string;
  outcome: string;
  guidance_family: string;
  route_key: string;
  rules_version: string;
  form_id: string;
  form_type: string;
  audience: "individual" | "team";
  failure_code: string;
}>;

const JOURNEY_EVENT_NAMES = new Set<JourneyEventName>([
  "persona_selected",
  "pathway_started",
  "pathway_step_completed",
  "pathway_result_viewed",
  "pathway_course_clicked",
  "pathway_team_redirected",
  "pathway_enquiry_form_started",
  "team_enquiry_started",
  "generate_lead",
  "enquiry_submit_failed",
  "form_submission_failed",
]);

const JOURNEY_EVENT_KEYS = new Set<keyof JourneyEventProps>([
  "persona",
  "placement",
  "destination_page_key",
  "entry_page_key",
  "page_key",
  "course_key",
  "step_key",
  "booking_intent",
  "outcome",
  "guidance_family",
  "route_key",
  "rules_version",
  "form_id",
  "form_type",
  "audience",
  "failure_code",
]);

const SAFE_JOURNEY_TOKEN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/;

function hasJourneyAnalyticsConsent(): boolean {
  const consentRequired =
    process.env.NEXT_PUBLIC_REQUIRE_MARKETING_CONSENT === "true";
  try {
    const choice = window.localStorage.getItem("cc.marketing-consent");
    if (choice === "denied") return false;
    return !consentRequired || choice === "granted";
  } catch {
    return !consentRequired;
  }
}

/** Dispatch an allowlisted event to the site's existing, consent-gated providers. */
export function trackJourneyEvent(
  eventName: JourneyEventName,
  props: JourneyEventProps = {},
) {
  if (
    typeof window === "undefined" ||
    !JOURNEY_EVENT_NAMES.has(eventName) ||
    !hasJourneyAnalyticsConsent()
  ) {
    return;
  }

  const safeProps: Record<string, string> = {};
  for (const [key, value] of Object.entries(props)) {
    if (
      JOURNEY_EVENT_KEYS.has(key as keyof JourneyEventProps) &&
      typeof value === "string" &&
      SAFE_JOURNEY_TOKEN.test(value)
    ) {
      safeProps[key] = value;
    }
  }

  if (window.gtag) {
    try {
      window.gtag("event", eventName, safeProps);
    } catch {
      // Analytics must never interrupt a navigation, quiz, or enquiry flow.
    }
  }
  if ((window as any).heap?.track) {
    try {
      (window as any).heap.track(eventName, safeProps);
    } catch {
      // Keep provider failures isolated from the core user journey.
    }
  }
}

/** Regulation-page conversion events. Never include contact details or free text. */
export type AestheticsEventName =
  | "call_admissions_click"
  | "callback_submit"
  | "start_point_select"
  | "course_book_click"
  | "package_add_to_basket"
  | "payment_plan_select"
  | "regulation_source_click"
  | "calendar_2027_click";

const AESTHETICS_EVENTS = new Set<AestheticsEventName>([
  "call_admissions_click", "callback_submit", "start_point_select",
  "course_book_click", "package_add_to_basket", "payment_plan_select",
  "regulation_source_click", "calendar_2027_click",
]);

export function trackAestheticsEvent(
  eventName: AestheticsEventName,
  props: Partial<Record<"placement" | "route" | "level" | "source" | "months", string>> = {},
) {
  if (typeof window === "undefined" || !AESTHETICS_EVENTS.has(eventName) || !hasJourneyAnalyticsConsent()) return;
  const allowedKeys = new Set(["placement", "route", "level", "source", "months"]);
  const safeProps = Object.fromEntries(Object.entries(props).filter(([key, value]) =>
    allowedKeys.has(key) && typeof value === "string" && SAFE_JOURNEY_TOKEN.test(value),
  ));
  try {
    window.gtag?.("event", eventName, { page_key: "pathway_to_aesthetics", ...safeProps });
  } catch {
    // Tracking must never block booking or navigation.
  }
}

export function trackPersonaSelected(params: {
  persona: string;
  placement: string;
  destination: string;
}) {
  const destinationPageKey = params.destination
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, "")
    .replace(/\//g, "_") || "home";

  trackJourneyEvent("persona_selected", {
    persona: params.persona,
    placement: params.placement,
    destination_page_key: destinationPageKey,
  });
}

interface FooterClickParams {
  linkText: string;
  linkHref: string;
  linkGroup: string;
  seoTopic?: string;
  seoIntent?: string;
}

interface ResourceToolUseParams {
  toolName: string;
  sourceField: string;
}

interface ResourceToolCTAParams {
  toolName: string;
  ctaText: string;
  ctaUrl: string;
  ctaType: string;
}

interface EbookCtaClickParams {
  ebookSlug: string;
  ebookTitle?: string;
  ctaText: string;
  ctaUrl: string;
  location: string;
  ctaType: "download" | "course" | "callback" | "resource";
}

export function trackFooterClick(params: FooterClickParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "footer_click", {
      link_text: params.linkText,
      link_href: params.linkHref,
      link_group: params.linkGroup,
      seo_topic: params.seoTopic || "",
      seo_intent: params.seoIntent || "",
    });
  }
}

export function trackResourceToolUse(params: ResourceToolUseParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "resource_tool_use", {
      tool_name: params.toolName,
      source_field: params.sourceField,
    });
  }
}

export function trackResourceToolCTA(params: ResourceToolCTAParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "resource_tool_cta_click", {
      tool_name: params.toolName,
      cta_text: params.ctaText,
      cta_url: params.ctaUrl,
      cta_type: params.ctaType,
    });
  }
}

export function trackEbookCtaClick(params: EbookCtaClickParams) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "ebook_cta_click", {
      ebook_slug: params.ebookSlug,
      ebook_title: params.ebookTitle || "",
      cta_text: params.ctaText,
      cta_url: params.ctaUrl,
      cta_location: params.location,
      cta_type: params.ctaType,
    });
  }
}

interface EbookDownloadParams {
  ebookSlug: string;
  ebookTitle: string;
  funnelRole: string;
  journeyKey?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  ebookLandingPage?: string;
  firstTouchLandingPage?: string;
  referrer?: string;
  leadId?: string | null;
}

export function trackEbookDownload(params: EbookDownloadParams) {
  if (typeof window === "undefined") {
    return;
  }

  const eventPayload = {
    ebook_slug: params.ebookSlug,
    ebook_title: params.ebookTitle,
    funnel_role: params.funnelRole,
    ebook_journey: params.journeyKey || "",
    utm_source: params.utmSource || "",
    utm_medium: params.utmMedium || "",
    utm_campaign: params.utmCampaign || "",
    utm_term: params.utmTerm || "",
    utm_content: params.utmContent || "",
    ebook_landing_page: params.ebookLandingPage || "",
    first_touch_landing_page: params.firstTouchLandingPage || "",
    referrer: params.referrer || "",
    lead_id: params.leadId || "",
  };

  if (window.gtag) {
    window.gtag("event", "ebook_download", eventPayload);
    window.gtag("event", "generate_lead", {
      ...eventPayload,
      currency: "GBP",
      value: 0,
    });
    window.gtag("set", {
      ebook_slug: params.ebookSlug,
      ebook_journey: params.journeyKey || "",
      first_touch_landing_page: params.firstTouchLandingPage || "",
      ebook_landing_page: params.ebookLandingPage || "",
    });
  }

  const fbq = (window as { fbq?: (...args: unknown[]) => void }).fbq;
  if (fbq) {
    fbq("track", "Lead", {
      content_name: params.ebookTitle,
      content_category: "Ebook",
      content_ids: [params.ebookSlug],
      value: 0,
      currency: "GBP",
    });
  }
}
